import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./ResponsableInformation.module.sass";
import Card from "../../../components/Card";
import TextInput from "../../../components/TextInput";
import { getPedagogical } from "../../../services/user.service";
import { updateFormation } from "../../../services/formation.service";
import { showSuccess, showError } from "../../../services/toast.service";
import LetterAvatar from "../../../components/LetterAvatar";
import { pb } from "../../../services/pocketbase";

const ResponsableInformations = ({ className, formation, onFormationChange, loading }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedResponsable, setSelectedResponsable] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Initialiser le responsable sélectionné si la formation en a déjà un
  useEffect(() => {
    if (formation?.pedagogical_id) {
      // Si on a déjà un responsable pédagogique dans la formation
      const fetchResponsable = async () => {
        try {
          // Récupérer le partner_id de l'utilisateur connecté
          let partnerId = null;
          if (pb.authStore.isValid && pb.authStore.model) {
            partnerId = pb.authStore.model.partner_id;
          }

          const results = await getPedagogical();
          const existingResponsable = results.find(user => user.id === formation.pedagogical_id);
          if (existingResponsable) {
            setSelectedResponsable(existingResponsable);
            // Utiliser first_name et last_name au lieu de name
            const fullName = `${existingResponsable.first_name || ''} ${existingResponsable.last_name || ''}`.trim();
            const email = existingResponsable.expand?.user_id?.email || '';
            setSearchQuery(fullName || email);
          }
        } catch (error) {
          console.error('Erreur lors de la récupération du responsable pédagogique:', error);
        }
      };

      fetchResponsable();
    }
  }, [formation?.pedagogical_id]);

  // Charger tous les responsables pédagogiques du partner_id une seule fois
  const [allResponsables, setAllResponsables] = useState([]);

  useEffect(() => {
    const loadAllResponsables = async () => {
      setIsLoading(true);
      try {
        // Récupérer le partner_id de l'utilisateur connecté
        let partnerId = null;
        if (pb.authStore.isValid && pb.authStore.model) {
          partnerId = pb.authStore.model.partner_id;
          console.log('Partner ID récupéré:', partnerId);
        }

        // Appeler getPedagogical
        const results = await getPedagogical();
        console.log('Responsables pédagogiques chargés pour le partner_id:', partnerId, 'Nombre:', results.length, results);
        setAllResponsables(results || []);
      } catch (error) {
        console.error('Erreur lors du chargement des responsables pédagogiques:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAllResponsables();
  }, []);

  // Filtrer les résultats en fonction de la recherche
  useEffect(() => {
    if (searchQuery.length >= 2 && allResponsables.length > 0) {
      const searchLower = searchQuery.toLowerCase();
      const filtered = allResponsables.filter(user => {
        const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
        const email = user.expand?.user_id?.email || '';
        return (
          (fullName && fullName.toLowerCase().includes(searchLower)) ||
          (user.first_name && user.first_name.toLowerCase().includes(searchLower)) ||
          (user.last_name && user.last_name.toLowerCase().includes(searchLower)) ||
          (email && email.toLowerCase().includes(searchLower))
        );
      });
      
      setSearchResults(filtered);
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [searchQuery, allResponsables]);

  // Gérer la sélection d'un responsable pédagogique
  const handleSelectResponsable = (responsable) => {
    setSelectedResponsable(responsable);
    // Utiliser first_name et last_name pour construire le nom complet
    const fullName = `${responsable.first_name || ''} ${responsable.last_name || ''}`.trim();
    const email = responsable.expand?.user_id?.email || '';
    setSearchQuery(fullName || email);
    setShowResults(false);

    if (onFormationChange) {
      // Créer un faux événement avec target.name et target.value
      onFormationChange({
        target: {
          name: 'pedagogical_id',
          value: responsable.id
        }
      });
    }
  };

  // Gérer le changement dans la barre de recherche
  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    
    // Si on modifie le texte de recherche et qu'un responsable est sélectionné,
    // on vérifie si le texte correspond encore au responsable sélectionné
    if (selectedResponsable) {
      const fullName = `${selectedResponsable.first_name || ''} ${selectedResponsable.last_name || ''}`.trim();
      const email = selectedResponsable.expand?.user_id?.email || '';
      
      // Si le texte ne correspond plus au responsable sélectionné, on le désélectionne
      // pour permettre une nouvelle recherche
      if (newValue !== fullName && newValue !== email) {
        setSelectedResponsable(null);
        
        // S'assurer que la liste des responsables est toujours disponible
        if (allResponsables.length === 0) {
          const reloadResponsables = async () => {
            try {
              let partnerId = null;
              if (pb.authStore.isValid && pb.authStore.model) {
                partnerId = pb.authStore.model.partner_id;
              }
              const results = await getPedagogical();
              setAllResponsables(results || []);
            } catch (error) {
              console.error('Erreur lors du rechargement automatique:', error);
            }
          };
          reloadResponsables();
        }
        
        // Mettre à jour la formation pour supprimer l'ancien responsable
        if (onFormationChange) {
          onFormationChange({
            target: {
              name: 'pedagogical_id',
              value: null
            }
          });
        }
      }
    }
  };
  
  // Fonction pour mettre à jour le responsable pédagogique
  const [updatingResponsable, setUpdatingResponsable] = useState(false);
  
  const handleUpdateResponsable = async () => {
    if (!formation?.id || !selectedResponsable) return;
    
    setUpdatingResponsable(true);
    try {
      // Mettre à jour uniquement le champ pedagogical_id
      await updateFormation(formation.id, { pedagogical_id: selectedResponsable.id });
      showSuccess("Responsable pédagogique mis à jour avec succès!");
      
      // Mettre à jour l'état local si nécessaire
      if (onFormationChange) {
        onFormationChange({
          target: {
            name: 'pedagogical_id',
            value: selectedResponsable.id
          }
        });
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du responsable pédagogique:', error);
      showError("Impossible de mettre à jour le responsable pédagogique: " + (error.message || "Une erreur est survenue"));
    } finally {
      setUpdatingResponsable(false);
    }
  };

  return (
    <Card
      className={cn(styles.card, className)}
      title="Responsable pédagogique"
      classTitle="title-yellow"
    >
      <div className={styles.fieldset}>
        <div className={styles.searchContainer}>
          <TextInput
            className={styles.field}
            label="Rechercher un responsable pédagogique"
            name="responsable-search"
            type="text"
            placeholder="Nom ou email du responsable (min. 2 caractères)"
            value={searchQuery}
            onChange={handleSearchChange}
          />

          {isLoading && (
            <div className={styles.loading}>Recherche en cours...</div>
          )}

          {showResults && searchResults.length > 0 && (
            <div className={styles.searchResults}>
              {searchResults.map((user, index) => (
                <div
                  key={user.id || index}
                  className={styles.resultItem}
                  onClick={() => handleSelectResponsable(user)}
                >
                  <div className={styles.resultContent}>

                    <div style={{ marginRight: '20px' }}>
                      <LetterAvatar
                        firstName={user.first_name}
                        lastName={user.last_name}
                        size={40}
                      />
                    </div>
                    <div className={styles.userInfo}>
                      <div className={styles.userName}>{`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Sans nom'}</div>
                      <div className={styles.userEmail}>{user.expand?.user_id?.email || 'Email non disponible'}</div>
                      {user.expand?.center_id && (
                        <div className={styles.userCenter}>
                          Centre: {user.expand.center_id.name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showResults && searchResults.length === 0 && searchQuery.length >= 2 && !isLoading && (
            <div className={styles.noResults}>
              Aucun responsable pédagogique trouvé
            </div>
          )}
        </div>

        {selectedResponsable && (
          <div className={styles.selectedUserDetails}>
            <h3>Responsable pédagogique sélectionné</h3>
            <div className={styles.selectedAvatar}>
              <LetterAvatar name={`${selectedResponsable.first_name || ''} ${selectedResponsable.last_name || ''}`.trim() || 'Sans nom'} size="large" />
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>Nom:</span>
              <span className={styles.value}>{`${selectedResponsable.first_name || ''} ${selectedResponsable.last_name || ''}`.trim() || 'Non renseigné'}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>Email:</span>
              <span className={styles.value}>{selectedResponsable.expand?.user_id?.email || 'Email non disponible'}</span>
            </div>
            {selectedResponsable.expand?.user_id?.phone && (
              <div className={styles.detailRow}>
                <span className={styles.label}>Téléphone:</span>
                <span className={styles.value}>{selectedResponsable.expand.user_id.phone}</span>
              </div>
            )}
            {selectedResponsable.expand?.center_id && (
              <div className={styles.detailRow}>
                <span className={styles.label}>Centre:</span>
                <span className={styles.value}>{selectedResponsable.expand.center_id.name}</span>
              </div>
            )}
          </div>
        )}

        {selectedResponsable && (
          <div className={styles.actions}>
            <div>
              <button
                className={cn("button button-stroke", styles.button)}
                type="button"
                onClick={() => {
                  // Réinitialiser la sélection
                  setSelectedResponsable(null);
                  setSearchQuery('');
                  setShowResults(false);
                  setSearchResults([]);
                  
                  // Recharger la liste des responsables pour s'assurer qu'elle est disponible
                  const reloadResponsables = async () => {
                    try {
                      let partnerId = null;
                      if (pb.authStore.isValid && pb.authStore.model) {
                        partnerId = pb.authStore.model.partner_id;
                      }
                      const results = await getPedagogical();
                      setAllResponsables(results || []);
                    } catch (error) {
                      console.error('Erreur lors du rechargement des responsables:', error);
                    }
                  };
                  reloadResponsables();
                  
                  // Mettre à jour la formation
                  onFormationChange({
                    target: {
                      name: 'pedagogical_id',
                      value: null
                    }
                  });
                }}
              >
                Réinitialiser la sélection
              </button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default ResponsableInformations;
